import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  faArrowTrendUp,
  faMoneyBill1Wave,
  faMoneyBillTrendUp,
  faSpinner,
  faTrash,
  faVault,
  faWallet,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { api } from 'api/api';
import { getTransactions, deleteTransaction } from '@redux/features/transactionsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { getCashboxes } from '@redux/features/cashboxesSlice';
import { formatKyivDate } from 'helpers/formatKyivDate';
import { MoneyAmount } from 'components/MoneyAmount';
import { Tooltip } from 'components/Tooltip';
import { Loader } from 'components/Loader';
import { GroupedTransactionModal } from 'pages/CashRegister/parts/GroupedTransactionModal';
import { CreateTransaction } from 'pages/CashRegister/parts/CreateTransaction';
import { Summary } from 'pages/CashRegister/parts/Summary';
import { Safe } from 'pages/CashRegister/parts/Safe';
import { Header } from 'pages/CashRegister/Header';
import styles from 'pages/CashRegister/CashRegister.scss';

export const CashRegister = () => {
  const { transactions, isLoading } = useSelector((state) => state.transactions);
  const { paymentMethods } = useSelector((state) => state.paymentMethods);
  const { cashboxes } = useSelector((state) => state.cashboxes);
  const { locations } = useSelector((state) => state.locations);

  const [highlightedTransactionID, setHighlightedTransactionID] = useState(null);
  const [balances, setBalances] = useState({ register: null, safe: null });
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [balancesError, setBalancesError] = useState(null);
  const [showSafeForm, setShowSafeForm] = useState(false);

  const transactionsEndRef = useRef(null);
  const transactionsFetched = useRef(false);
  const prevLocationID = useRef(null);
  const prevDate = useRef(null);
  const balancesFetchController = useRef(null);
  const balancesDebounceTimer = useRef(null);

  const query = useSearchParamsUpdater();
  const { locationName } = useParams();
  const dispatch = useDispatch();

  const locationTransliteration = query.getParam('location') || locationName;
  const searchDate = query.getParam('date');
  const isToday = searchDate === formatKyivDate(new Date());

  useEffect(() => {
    if (balancesError) {
      dispatch(addNotification({ id: Date.now(), title: balancesError, type: 'error' }));
    }
  }, [balancesError, dispatch]);

  const location = useMemo(
    () => locations.find(({ transliteration }) => transliteration === locationTransliteration),
    [locations, locationTransliteration]
  );

  const locationCashboxes = useMemo(
    () => cashboxes.filter((cb) => cb.location?.id === location?.id),
    [cashboxes, location?.id]
  );

  const cashRegister = useMemo(() => locationCashboxes.find((cb) => cb.type === 'register'), [locationCashboxes]);

  const safe = useMemo(() => locationCashboxes.find((cb) => cb.type === 'safe'), [locationCashboxes]);

  const currentLocationPaymentMethods = useMemo(
    () =>
      paymentMethods.filter(
        (m) => m.value !== 'cash' || m.cashbox?.location?.transliteration === locationTransliteration
      ),
    [paymentMethods, locationTransliteration]
  );

  useEffect(() => {
    if (location?.id) {
      dispatch(getCashboxes(location.id));
    }
  }, [location?.id, dispatch]);

  useEffect(() => {
    if (!cashRegister?.id || !searchDate || !location?.id) return;

    const locationChanged = prevLocationID.current !== location.id;
    const dateChanged = prevDate.current !== searchDate;
    const firstLoad = !transactionsFetched.current;

    if (firstLoad || dateChanged || locationChanged) {
      dispatch(
        getTransactions({
          locationID: location.id,
          cashboxID: cashRegister.id,
          date: searchDate,
          includeAccounts: true,
          sortOrder: 'asc',
          limit: 100,
        })
      );

      transactionsFetched.current = true;
      prevDate.current = searchDate;
      prevLocationID.current = location.id;
    }
  }, [cashRegister?.id, searchDate, location?.id, dispatch]);

  const fetchBalanceForDate = useCallback(async (cashboxID, date, signal) => {
    if (!cashboxID || !date) return null;

    try {
      const response = await api.get(`/cashboxes/${cashboxID}/balance`, {
        params: { date },
        signal,
      });

      if (response.data) {
        return {
          start: response.data.balanceStart,
          end: response.data.balanceEnd || response.data.currentBalance,
          isClosed: response.data.isClosed,
        };
      }

      return null;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        throw err;
      }
      console.error('Error fetching balance:', err);
      return null;
    }
  }, []);

  const fetchAllBalances = useCallback(async () => {
    if ((!cashRegister?.id && !safe?.id) || !searchDate) return;

    if (balancesFetchController.current) {
      balancesFetchController.current.abort();
    }

    balancesFetchController.current = new AbortController();
    const { signal } = balancesFetchController.current;

    setIsLoadingBalances(true);
    setBalancesError(null);

    try {
      const [registerResult, safeResult] = await Promise.allSettled([
        cashRegister?.id ? fetchBalanceForDate(cashRegister.id, searchDate, signal) : null,
        safe?.id ? fetchBalanceForDate(safe.id, searchDate, signal) : null,
      ]);

      setBalances({
        register: registerResult.status === 'fulfilled' ? registerResult.value : null,
        safe: safeResult.status === 'fulfilled' ? safeResult.value : null,
      });

      if (registerResult.status === 'rejected' && safeResult.status === 'rejected') {
        setBalancesError('Не вдалося завантажити баланси');
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      setBalancesError(err.message || 'Помилка завантаження балансів');
    } finally {
      setIsLoadingBalances(false);
      balancesFetchController.current = null;
    }
  }, [cashRegister?.id, safe?.id, searchDate, fetchBalanceForDate]);

  const debouncedFetchBalances = useCallback(() => {
    if (balancesDebounceTimer.current) {
      clearTimeout(balancesDebounceTimer.current);
    }

    balancesDebounceTimer.current = setTimeout(() => {
      fetchAllBalances();
    }, 300);
  }, [fetchAllBalances]);

  useEffect(() => {
    if (isToday && (cashRegister || safe)) {
      const initialBalances = {
        register: null,
        safe: null,
      };

      if (cashRegister?.balanceDetails) {
        initialBalances.register = {
          start: cashRegister.balanceDetails.balanceStart,
          end: cashRegister.balance,
          isClosed: false,
        };
      } else if (cashRegister?.balance !== undefined) {
        initialBalances.register = {
          start: cashRegister.balance,
          end: cashRegister.balance,
          isClosed: false,
        };
      }

      if (safe?.balanceDetails) {
        initialBalances.safe = {
          start: safe.balanceDetails.balanceStart,
          end: safe.balance,
          isClosed: false,
        };
      } else if (safe?.balance !== undefined) {
        initialBalances.safe = {
          start: safe.balance,
          end: safe.balance,
          isClosed: false,
        };
      }

      if (initialBalances.register || initialBalances.safe) {
        setBalances(initialBalances);
      }
    }

    debouncedFetchBalances();

    return () => {
      if (balancesFetchController.current) {
        balancesFetchController.current.abort();
      }
      if (balancesDebounceTimer.current) {
        clearTimeout(balancesDebounceTimer.current);
      }
    };
  }, [cashRegister, safe, debouncedFetchBalances]);

  const handleDeleteTransaction = useCallback(
    async (id) => {
      const result = await dispatch(deleteTransaction(id));
      if (result) {
        await dispatch(getCashboxes(location?.id));
        fetchAllBalances();
      }
    },
    [dispatch, location?.id, fetchAllBalances]
  );

  const scrollToBottom = useCallback(() => {
    transactionsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const groupedTransactions = useMemo(() => {
    const groups = new Map();

    transactions.forEach((transaction) => {
      const payerID = transaction.payer?.id;
      const methodID = transaction.method?.id;
      const transactionType = transaction.type;

      if (payerID == null || !methodID) {
        groups.set(transaction.id, {
          ...transaction,
          isGrouped: false,
          groupedCount: 1,
          originalTransactions: [transaction],
        });
        return;
      }

      const groupKey = `${payerID}-${methodID}-${transactionType}`;

      if (groups.has(groupKey)) {
        const existingGroup = groups.get(groupKey);
        existingGroup.amount += transaction.amount;
        existingGroup.groupedCount += 1;
        existingGroup.originalTransactions.push(transaction);
      } else {
        groups.set(groupKey, {
          ...transaction,
          isGrouped: false,
          groupedCount: 1,
          originalTransactions: [transaction],
        });
      }
    });

    const result = Array.from(groups.values()).map((group) => ({
      ...group,
      isGrouped: group.groupedCount > 1,
    }));

    return result;
  }, [transactions]);

  const handleTransactionCreated = useCallback(
    (newTransactionID) => {
      setHighlightedTransactionID(newTransactionID);
      scrollToBottom();
      setShowSafeForm(false);

      debouncedFetchBalances();

      setTimeout(() => {
        setHighlightedTransactionID(null);
      }, 2000);
    },
    [scrollToBottom, debouncedFetchBalances]
  );

  return (
    <>
      <Header showSafeForm={showSafeForm} setShowSafeForm={setShowSafeForm} />

      <div className={styles.content}>
        <div className={styles.column}>
          <Summary
            methods={currentLocationPaymentMethods}
            transactions={transactions}
            searchDate={searchDate}
            isLoading={isLoading}
          />

          <CreateTransaction
            locationID={location?.id}
            cashRegisterID={cashRegister?.id}
            onTransactionCreated={handleTransactionCreated}
          />

          <Safe
            showSafeForm={showSafeForm}
            safe={safe}
            safeBalance={balances.safe?.end}
            cashRegister={cashRegister}
            cashRegisterBalance={balances.register?.end}
            locationID={location?.id}
            onTransactionCreated={handleTransactionCreated}
          />
        </div>

        <div>
          <div className={styles.transactionsHeader}>
            <div className={styles.transactionTitleWrapper}>
              <h1>
                <FontAwesomeIcon icon={faMoneyBillTrendUp} />
                Транзакції за день
              </h1>
              <p>Оплати у розкладі та інші створені фінансові операції</p>
            </div>

            <div className={styles.balanceBadges}>
              <div className={cn(styles.badge, styles.badgeBlue)}>
                <FontAwesomeIcon
                  icon={isLoadingBalances ? faSpinner : faWallet}
                  className={styles.badgeIcon}
                  spin={isLoadingBalances}
                />
                <div className={styles.badgeContent}>
                  <div className={styles.badgeLabel}>Початок</div>
                  <div className={styles.badgeValue}>
                    <MoneyAmount value={balances.register?.start || 0} />
                  </div>
                </div>
              </div>

              <div className={cn(styles.badge, styles.badgeGreen)}>
                <FontAwesomeIcon
                  icon={isLoadingBalances ? faSpinner : faArrowTrendUp}
                  className={styles.badgeIcon}
                  spin={isLoadingBalances}
                />
                <div className={styles.badgeContent}>
                  <div className={styles.badgeLabel}>Кінець</div>
                  <div className={styles.badgeValue}>
                    <MoneyAmount value={balances.register?.end || 0} />
                  </div>
                </div>
              </div>

              <div className={cn(styles.badge, styles.badgePurple)}>
                <FontAwesomeIcon
                  icon={isLoadingBalances ? faSpinner : faVault}
                  className={styles.badgeIcon}
                  spin={isLoadingBalances}
                />
                <div className={styles.badgeContent}>
                  <div className={styles.badgeLabel}>Сейф</div>
                  <div className={styles.badgeValue}>
                    <MoneyAmount value={balances.safe?.end || 0} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.transactions}>
            <div className={cn(styles.tableRow, styles.tableRowHeader)}>
              <div className={styles.tableCell}>Опис</div>
              <div className={styles.tableCell}>Платник</div>
              <div className={styles.tableCell}>Сума</div>
              <div className={styles.tableCell}>Спосіб</div>
              <div className={styles.tableCell}>Адміністратор</div>
              <div className={styles.actionCell}>Дії</div>
            </div>
            {!isLoading && transactions.length === 0 && (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faMoneyBill1Wave} className={styles.emptyStateIcon} />
                {isToday ? (
                  <>
                    <h3 className={styles.emptyStateTitle}>За сьогодні транзакцій ще не було</h3>
                    <p className={styles.emptyStateText}>
                      Каса відпочиває... Може, саме час створити першу транзакцію? 😉
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className={styles.emptyStateTitle}>За цей день нічого не сталося</h3>
                    <p className={styles.emptyStateText}>
                      Можливо, це був вихідний. Або гроші просто вирішили зробити собі digital-детокс 💤
                    </p>
                  </>
                )}
              </div>
            )}

            {isLoading && (
              <div className={styles.loader}>
                <Loader isActive size={4} />
                Завантаження транзакцій...
              </div>
            )}

            {groupedTransactions.map((t) => (
              <div
                key={t.id}
                className={cn(styles.tableRow, {
                  [styles.highlighted]: t.id === highlightedTransactionID,
                  [styles.grouped]: t.isGrouped,
                })}
              >
                <div className={cn(styles.tableCell, styles.description)}>
                  {t.description}
                  {t.isGrouped && (
                    <div className={styles.groupBadgeWrapper}>
                      <span className={styles.groupBadge}>{t.groupedCount}</span>
                      <Tooltip
                        className={styles.groupBadgeTooltip}
                        text='Транзакції з однаковим платником і способом оплати автоматично об’єднуються в одну групу'
                        position='right'
                      />
                    </div>
                  )}
                </div>
                <div className={styles.tableCell}>
                  {t.payer?.surname} {t.payer?.name}
                </div>
                <div
                  className={cn(styles.tableCell, {
                    [styles.expense]: t.type === 'expense' || t.type === 'transfer_out',
                  })}
                >
                  <MoneyAmount
                    className={styles.amount}
                    value={t.type === 'expense' || t.type === 'transfer_out' ? -t.amount : t.amount}
                  />
                </div>
                <div className={styles.tableCell}>{t.method?.label}</div>
                <div className={styles.tableCell}>
                  {t.operator?.surname} {t.operator?.name?.[0]}.
                </div>
                <div className={styles.actionCell}>
                  {t.isGrouped && (
                    <button
                      onClick={() => setSelectedGroup(t)}
                      className={styles.viewGroupBtn}
                      title='Переглянути всі транзакції в групі'
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  )}
                  <button onClick={() => handleDeleteTransaction(t.id)} className={styles.deleteBtn}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
            <div ref={transactionsEndRef} />
          </div>
        </div>
      </div>

      <GroupedTransactionModal
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        handleDeleteTransaction={handleDeleteTransaction}
      />
    </>
  );
};
